import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Calendar, Clock, LogOut, User, CheckCircle, AlertCircle, Loader2, CreditCard, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import axios from 'axios';
import { downloadReceipt } from '../lib/receipt';
import { ClientNextAppointment } from '../components/appointments/ClientNextAppointment';
import { formatAppt } from '../components/appointments/apptI18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  aperta: 'bg-blue-100 text-blue-700',
  in_lavorazione: 'bg-yellow-100 text-yellow-700',
  da_firmare: 'bg-orange-100 text-orange-700',
  completata: 'bg-green-100 text-green-700',
  archiviata: 'bg-slate-100 text-slate-700'
};

const statusLabels = {
  it: { aperta: 'Aperta', in_lavorazione: 'In Lavorazione', da_firmare: 'Da Firmare', completata: 'Completata', archiviata: 'Archiviata' },
  en: { aperta: 'Open', in_lavorazione: 'In Progress', da_firmare: 'To Sign', completata: 'Completed', archiviata: 'Archived' },
  de: { aperta: 'Offen', in_lavorazione: 'In Bearbeitung', da_firmare: 'Zu Unterschreiben', completata: 'Abgeschlossen', archiviata: 'Archiviert' },
  fr: { aperta: 'Ouvert', in_lavorazione: 'En Cours', da_firmare: 'À Signer', completata: 'Terminé', archiviata: 'Archivé' }
};

const translations = {
  it: {
    title: 'Le Mie Pratiche',
    welcome: 'Benvenuto',
    noPratiche: 'Nessuna pratica trovata',
    documents: 'Documenti',
    noDocuments: 'Nessun documento disponibile',
    download: 'Scarica',
    appointment: 'Prossimo Appuntamento',
    notes: 'Note',
    logout: 'Esci',
    loading: 'Caricamento...',
    payments: 'Pagamenti',
    noPayments: 'Nessun pagamento richiesto',
    pay: 'Paga Ora',
    paid: 'Pagato',
    pending: 'In Attesa',
    paymentTab: 'Pagamenti',
    praticheTab: 'Pratiche',
    area: 'Area Riservata',
    numero: 'Numero Pratica',
    tipo: 'Tipo'
  },
  en: {
    title: 'My Cases',
    welcome: 'Welcome',
    noPratiche: 'No cases found',
    documents: 'Documents',
    noDocuments: 'No documents available',
    download: 'Download',
    appointment: 'Next Appointment',
    notes: 'Notes',
    logout: 'Logout',
    loading: 'Loading...',
    payments: 'Payments',
    noPayments: 'No payments requested',
    pay: 'Pay Now',
    paid: 'Paid',
    pending: 'Pending',
    paymentTab: 'Payments',
    praticheTab: 'Cases',
    area: 'Client Area',
    numero: 'Case Number',
    tipo: 'Type'
  },
  de: {
    title: 'Meine Fälle',
    welcome: 'Willkommen',
    noPratiche: 'Keine Fälle gefunden',
    documents: 'Dokumente',
    noDocuments: 'Keine Dokumente verfügbar',
    download: 'Herunterladen',
    appointment: 'Nächster Termin',
    notes: 'Notizen',
    logout: 'Abmelden',
    loading: 'Laden...',
    payments: 'Zahlungen',
    noPayments: 'Keine Zahlungen angefordert',
    pay: 'Jetzt bezahlen',
    paid: 'Bezahlt',
    pending: 'Ausstehend',
    paymentTab: 'Zahlungen',
    praticheTab: 'Fälle',
    area: 'Kundenbereich',
    numero: 'Fallnummer',
    tipo: 'Art'
  },
  fr: {
    title: 'Mes Dossiers',
    welcome: 'Bienvenue',
    noPratiche: 'Aucun dossier trouvé',
    documents: 'Documents',
    noDocuments: 'Aucun document disponible',
    download: 'Télécharger',
    appointment: 'Prochain Rendez-vous',
    notes: 'Notes',
    logout: 'Déconnexion',
    loading: 'Chargement...',
    payments: 'Paiements',
    noPayments: 'Aucun paiement demandé',
    pay: 'Payer maintenant',
    paid: 'Payé',
    pending: 'En attente',
    paymentTab: 'Paiements',
    praticheTab: 'Dossiers',
    area: 'Espace Client',
    numero: 'Numéro de dossier',
    tipo: 'Type'
  }
};

const tipoLabels = {
  en: { compravendita: 'Property sale', mutuo: 'Mortgage', successione: 'Succession', donazione: 'Donation', procura: 'Power of attorney', costituzione: 'Company formation', societa: 'Company', autentica: 'Authentication', altro: 'Other' },
  de: { compravendita: 'Kaufvertrag', mutuo: 'Darlehen', successione: 'Erbschaft', donazione: 'Schenkung', procura: 'Vollmacht', costituzione: 'Gesellschaftsgründung', societa: 'Gesellschaft', autentica: 'Beglaubigung', altro: 'Sonstiges' },
  fr: { compravendita: 'Vente immobilière', mutuo: 'Prêt hypothécaire', successione: 'Succession', donazione: 'Donation', procura: 'Procuration', costituzione: 'Constitution de société', societa: 'Société', autentica: 'Authentification', altro: 'Autre' }
};
const tipoLabel = (tipo, lang) => {
  const key = (tipo || '').toLowerCase().split(' ')[0].replace('à', 'a');
  return tipoLabels[lang]?.[key] || tipo;
};

const ClientAreaPage = () => {
  const { language, t: tr } = useLanguage();
  const t = translations[language] || translations.it;
  const statusLabel = statusLabels[language] || statusLabels.it;
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [pratiche, setPratiche] = useState([]);
  const [selectedPratica, setSelectedPratica] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [activeTab, setActiveTab] = useState('pratiche');
  const [payments, setPayments] = useState([]);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!storedUser || !token) {
      navigate('/login');
      return;
    }
    
    setUser(JSON.parse(storedUser));
    fetchPratiche();
    fetchPayments();
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) axios.post(`${API}/client/language`, { language }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  }, [language]);

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/client/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data);
    } catch (err) {
      console.error('Error fetching payments:', err);
    }
  };

  const handlePayment = async (paymentId) => {
    setPayingId(paymentId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/client/payments/checkout`, {
        payment_id: paymentId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Checkout error:', err);
      alert(tr('ui.clientArea.payError'));
      setPayingId(null);
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      await downloadReceipt(`${API}/client/payments/${paymentId}/receipt`);
    } catch (err) {
      alert(tr('ui.clientArea.receiptError'));
    }
  };

  const fetchPratiche = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/client/pratiche`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPratiche(response.data);
      if (response.data.length > 0) {
        setSelectedPratica(response.data[0]);
        fetchDocuments(response.data[0].id);
      }
    } catch (err) {
      console.error('Error fetching pratiche:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async (praticaId) => {
    setLoadingDocs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/client/pratiche/${praticaId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleSelectPratica = (pratica) => {
    setSelectedPratica(pratica);
    fetchDocuments(pratica.id);
  };

  const handleDownload = async (docId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/client/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const byteCharacters = atob(response.data.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.file_type });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-[#283880]" />
      </div>
    );
  }

  return (
    <div data-testid="client-area" className="min-h-screen bg-[#FAF8F5] pt-28 pb-16">
      <SEO page="home" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#283880] tracking-tight mb-1">
              {t.welcome}, {user?.name}
            </h1>
            <p className="text-[#5A6578] text-sm">{t.area}</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-btn"
            variant="outline"
            className="mt-4 md:mt-0 border-[#283880] text-[#283880] hover:bg-[#283880] hover:text-white rounded-none uppercase tracking-[0.15em] text-[10px] font-bold"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t.logout}
          </Button>
        </div>

        <ClientNextAppointment lang={language} />

        {/* Tabs */}
        <div className="flex gap-0 mb-8 border-b border-[rgba(30,36,51,0.1)]">
          <button
            onClick={() => setActiveTab('pratiche')}
            className={`px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
              activeTab === 'pratiche' ? 'text-[#283880] border-b-2 border-[#C9A227]' : 'text-[#5A6578] hover:text-[#283880]'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2 stroke-[1.5]" />
            {t.praticheTab || t.title}
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            data-testid="payments-tab"
            className={`px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] transition-colors relative ${
              activeTab === 'payments' ? 'text-[#283880] border-b-2 border-[#C9A227]' : 'text-[#5A6578] hover:text-[#283880]'
            }`}
          >
            <CreditCard className="w-4 h-4 inline mr-2 stroke-[1.5]" />
            {t.paymentTab || 'Pagamenti'}
            {payments.filter(p => p.status === 'pending' || p.status === 'initiated').length > 0 && (
              <span className="ml-2 bg-[#C9A227] text-[#121826] text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {payments.filter(p => p.status === 'pending' || p.status === 'initiated').length}
              </span>
            )}
          </button>
        </div>

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            {payments.length === 0 ? (
              <div className="bg-white border border-[rgba(30,36,51,0.08)] p-12 text-center">
                <CreditCard className="w-12 h-12 mx-auto text-[#5A6578]/30 mb-4 stroke-[1]" />
                <p className="text-[#5A6578]">{t.noPayments}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {payments.map((payment) => (
                  <div key={payment.id} className="bg-white border border-[rgba(30,36,51,0.08)] shadow-[0_2px_12px_rgba(40,56,128,0.04)] p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${
                            payment.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-[#C9A227]/10 text-[#C9A227]'
                          }`}>
                            {payment.status === 'paid' ? (t.paid || 'Pagato') : (t.pending || 'In Attesa')}
                          </span>
                          {payment.pratica_numero && (
                            <span className="text-xs text-[#5A6578] font-mono">{payment.pratica_numero}</span>
                          )}
                        </div>
                        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#283880] mb-1">
                          {payment.description}
                        </h3>
                        {payment.pratica_titolo && (
                          <p className="text-sm text-[#5A6578]">{payment.pratica_titolo}</p>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                        <span className="font-['Playfair_Display'] text-2xl font-bold text-[#283880] whitespace-nowrap">
                          &euro; {payment.amount?.toFixed(2)}
                        </span>
                        {(payment.status === 'pending' || payment.status === 'initiated') && (
                          <Button
                            onClick={() => handlePayment(payment.id)}
                            disabled={payingId === payment.id}
                            data-testid={`pay-btn-${payment.id}`}
                            className="bg-[#C9A227] text-[#121826] hover:bg-[#B38E1B] rounded-none uppercase tracking-[0.15em] text-[10px] font-bold h-11 px-6 shadow-[0_2px_12px_rgba(201,162,39,0.25)]"
                          >
                            {payingId === payment.id ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <ExternalLink className="w-4 h-4 mr-2" />
                            )}
                            {t.pay || 'Paga Ora'}
                          </Button>
                        )}
                        {payment.status === 'paid' && (
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-6 h-6 text-green-500" />
                            <Button
                              onClick={() => handleDownloadReceipt(payment.id)}
                              data-testid={`client-receipt-btn-${payment.id}`}
                              variant="outline"
                              className="rounded-none uppercase tracking-[0.15em] text-[10px] font-bold h-11 px-5 border-[#283880] text-[#283880] hover:bg-[#283880] hover:text-white"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              {tr('ui.clientArea.receipt')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pratiche Tab */}
        {activeTab === 'pratiche' && (
        <>
        {pratiche.length === 0 ? (
          <div className="bg-white border border-[rgba(30,36,51,0.08)] p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-[#5A6578]/30 mb-4 stroke-[1]" />
            <p className="text-[#5A6578]">{t.noPratiche}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Pratiche List */}
            <div className="lg:col-span-1 space-y-4">
              {pratiche.map((pratica) => (
                <motion.div
                  key={pratica.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => handleSelectPratica(pratica)}
                  className={`bg-white rounded-xl p-4 cursor-pointer transition-all border-2 ${
                    selectedPratica?.id === pratica.id
                      ? 'border-[#C9A227] shadow-lg'
                      : 'border-transparent hover:border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-[#283880] text-sm">{pratica.titolo}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors[pratica.status]}`}>
                      {statusLabel[pratica.status]}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">{pratica.numero_pratica}</p>
                  <p className="text-xs text-slate-400">{tipoLabel(pratica.tipo, language)}</p>
                </motion.div>
              ))}
            </div>

            {/* Pratica Details */}
            {selectedPratica && (
              <div className="lg:col-span-2 space-y-6">
                {/* Info Card */}
                <div className="bg-white rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-[#C9A227]" />
                    <h2 className="font-semibold text-[#283880]">{selectedPratica.titolo}</h2>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">{t.numero}</p>
                      <p className="font-mono text-sm">{selectedPratica.numero_pratica}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">{t.tipo}</p>
                      <p className="text-sm capitalize">{tipoLabel(selectedPratica.tipo, language)}</p>
                    </div>
                  </div>

                  {selectedPratica.appuntamento && (
                    <div className="bg-[#283880]/5 rounded-lg p-4 mb-4">
                      <div className="flex items-center gap-2 text-[#283880]">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm font-medium">{t.appointment}</span>
                      </div>
                      <p className="text-sm mt-2">
                        {formatAppt(/[zZ]|[+-]\d\d:\d\d$/.test(selectedPratica.appuntamento) ? selectedPratica.appuntamento : `${selectedPratica.appuntamento}Z`, language)}
                      </p>
                    </div>
                  )}

                  {selectedPratica.note_cliente && (
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-sm font-medium text-slate-700 mb-2">{t.notes}</p>
                      <p className="text-sm text-slate-600">{selectedPratica.note_cliente}</p>
                    </div>
                  )}
                </div>

                {/* Documents Card */}
                <div className="bg-white rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-5 h-5 text-[#C9A227]" />
                    <h2 className="font-semibold text-[#283880]">{t.documents}</h2>
                  </div>

                  {loadingDocs ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#283880]" />
                    </div>
                  ) : documents.length === 0 ? (
                    <p className="text-slate-400 text-sm text-center py-8">{t.noDocuments}</p>
                  ) : (
                    <div className="space-y-3">
                      {documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#283880] rounded flex items-center justify-center">
                              <FileText className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-700">{doc.original_filename}</p>
                              <p className="text-xs text-slate-400">
                                {(doc.file_size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleDownload(doc.id, doc.original_filename)}
                            size="sm"
                            className="bg-[#C9A227] hover:bg-[#b8911f] text-white"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            {t.download}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default ClientAreaPage;
